import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FullCalendarComponent } from '@fullcalendar/angular';
import dayjs from '../../config/dayjs.config';
import { RESERVATION_HOUR_OPTIONS } from '../../config/reservation-hours.constant';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgxCronUiConfig } from 'ngx-cron-ui/lib/model/model';
import { QuillEditorComponent } from 'ngx-quill';
import { Subscription } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import { Amenity, Product, ProductRepresentationPhotoCreateDto, ProductStatus } from 'src/app/model/product';
import { isRichHtml } from 'src/app/util/rich-html';
import { failIfSwallowed } from 'src/app/util/swallowed-http';
import { AuthService } from 'src/app/service/auth.service';
import { MessageService } from 'src/app/service/message.service';
import {
  controlAt,
  isExpectedPhotoTarget,
  PHOTO_MOVED_MESSAGE,
  toPreviewSections,
  toPreviewSummaries,
} from 'src/app/util/photo-upload';
import { Hashtag } from './../../model/product';
import { CheckDesktopService } from './../../service/check-desktop.service';
import { FileUploadService } from './../../service/file-upload.service';
import { FormService } from './../../service/form.service';
import { DiscountCouponCandidate, ProductService } from './../../service/product.service';

@Component({
  selector: 'meeting-control',
  templateUrl: './meeting-control.component.html',
  styleUrls: ['./meeting-control.component.scss'],
})
export class MeetingControlComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() quillStyle;
  @Input() hashtags: Hashtag[];
  @Input() amenities: Amenity[];
  @Input() latitude: number;
  @Input() longitude: number;
  @Input() zoom: number;
  @Input() previewProduct: Product;
  @Input() submitMessage: string;
  @Input() loadMessage: string;
  @Input() isLoad: boolean;
  @Input() hasUnsavedChanges: boolean = false;
  @Input() lastAutoSaved: Date | null = null;
  @Input() productStatus: string;
  /** 선택된 테마 id. 상품 수정 화면은 저장 본문에 실어 보내고, 등록 화면은 생성 후 별도 요청으로 붙인다. */
  @Input() themeIds: number[] = [];
  @Output() themeIdsChange = new EventEmitter<number[]>();
  /**
   * 테마 선택을 화면에 띄울지. **저장할 수 없는 상황이면 반드시 false여야 한다.**
   *
   * 수정 화면은 상세 응답에 `themes` 키가 없으면(구버전 서버) 테마를 아예 저장하지 않는데,
   * 그때도 칩을 띄우면 호스트가 고른 선택이 "저장되었습니다" 토스트와 함께 조용히 버려진다.
   * 기본값 true는 저장 경로가 항상 열려 있는 등록 화면 기준이다.
   */
  @Input() canSelectThemes: boolean = true;
  @Input() saveMessage: string = '';
  @Input() showSaveMessage: boolean = false;
  /**
   * 호스트 연락처(host_info.mobile) 인증 여부. false면 검수 요청 전에 안내를 띄운다.
   *
   * 기본값이 true인 이유는 안내를 띄우지 않는 쪽이 안전한 default이기 때문이다 —
   * 값을 넘기지 않는 호출자(어드민 상품 편집 등)에서 잘못된 경고가 뜨면 안 된다.
   * 실제 차단은 서버(`ProductService.requestReview`)가 하므로 이 값은 안내 전용이다.
   */
  @Input() isMobileVerified: boolean = true;
  /** 예약 가능한 일정(티켓)이 있는지. false면 검수 요청 전에 안내를 띄운다(NNB-679). */
  @Input() hasReservableTicket: boolean = true;
  /** 안내에서 일정 등록 화면으로 보낼 때 쓴다. 신규 등록 등 아직 id가 없으면 링크를 감춘다. */
  @Input() productId: number | null = null;
  /**
   * 상품의 예약 마감 시간(시). 게이트가 서버와 같은 기준으로 "예약을 받을 수 있는 회차"를 센다.
   * 같은 클래스의 `reservationHours` 는 드롭다운 옵션 목록이라 이름이 겹치지 않게 둔다.
   */
  @Input() productReservationHours = 0;
  /**
   * 예약 방식(즉시 확정/승인 후 확정) 토글 노출 여부.
   * 이 컴포넌트는 호스트·어드민 등록/수정 화면이 공유하지만, 토글은 어드민 수정 화면 전용이라
   * 기본값 false — 호출부가 명시적으로 넘겨야 보인다.
   */
  @Input() showBookingMode: boolean = false;

  @Output() onNextEvent = new EventEmitter();
  @Output() onPrevEvent = new EventEmitter();
  @Output() onLastCheckEvent = new EventEmitter();
  @Output() onChangeAddressEvent = new EventEmitter();
  @Output() onAddEvent = new EventEmitter();
  @Output() onSaveEvent = new EventEmitter();
  @Output() onRequestReviewEvent = new EventEmitter();
  /** 연락처 미인증 안내의 인증 버튼. 페이지를 떠나지 않도록 호출부가 바텀시트를 연다. */
  @Output() onVerifyMobileEvent = new EventEmitter();
  /** 게이트가 일정을 판매중으로 바꿨을 때. 호출부가 `hasReservableTicket`을 다시 조회한다. */
  @Output() onReservableChangeEvent = new EventEmitter();
  @Output() onQuillLoadEvent = new EventEmitter();

  ProductStatus = ProductStatus;

  /**
   * 검수 요청을 막는 사유를 안내할 화면인지. 검수 대기·판매 중에는 요청할 일이 없어 숨긴다 —
   * 그 화면에 "검수를 요청하려면"이 뜨면 모순된 안내가 된다.
   */
  get showsReviewBlockers(): boolean {
    return (
      !this.isAdminPage &&
      this.productStatus !== ProductStatus.PENDING_REVIEW &&
      this.productStatus !== ProductStatus.ENTERED &&
      // 종료됨에도 요청할 일이 없다. 게이트가 「판매중으로 바꾸기」를 띄우므로, 빠뜨리면 종료한
      // 상품의 회차를 되살리는 버튼이 생긴다(nnb-nextjs·파트너는 처음부터 세 상태를 모두 뺀다).
      this.productStatus !== ProductStatus.END
    );
  }

  addTag = true;
  MAX_HASHTAG_LENGTH = 20;
  isDesktop = false;
  isShowMenu = false;
  currentStep = 0; // Track current step for navigation buttons
  isAdminPage = false; // Check if we're in admin page

  /** 쿠폰 지정 UI(NNB-912) 후보. 어드민 화면에서만, productId 가 있어야 불러온다. */
  discountCouponCandidates: DiscountCouponCandidate[] = [];
  /** "지정 안 함(자동)" 을 고르면 될 가격 */
  discountAutoPrice: number | null = null;
  discountCandidatesLoading = false;
  /** 조회 실패와 "연결된 쿠폰 없음"은 다른 안내다 — 실패를 빈 목록으로 묻으면 잘못된 지시가 된다. */
  discountCandidatesFailed = false;

  // 미리보기 관련 속성들
  showPreview = true;
  livePreviewData: Partial<Product> = {};
  formChangeSubscription: Subscription;
  userSubscription: Subscription;
  timeUpdateInterval: any;

  @ViewChild('quill') quill: QuillEditorComponent;

  ngxCronUiConfig: NgxCronUiConfig = {
    option: { minute: false, hour: false, year: false },
    isSetDefaultValue: true,
    isBaseFrequencyNewLine: true,
  };

  @ViewChild('calendar') calendarComponent: FullCalendarComponent;
  days = [];
  hours = [];
  minutes = [];
  reservationHours: { label: string; value: number }[] = RESERVATION_HOUR_OPTIONS.map((value) => ({
    label: this.formatReservationHourLabel(value),
    value,
  }));

  modalRef: BsModalRef;

  noticeChecked = false;
  descriptions: {
    title: string;
    descList: string[];
    moreDescList?: string[];
    image?: string;
  }[];
  random = `${dayjs().format('YYYY-MM-DDTHH:mm:ss:SSS')}-${(Math.random() * 1000000).toFixed()}`;

  croppedImage;
  toProductRepresentationPhotoCreateDto = (data: any) => {
    return new ProductRepresentationPhotoCreateDto(data.url, 0, data.file);
  };
  isAdmin = false;

  /** 상세 설명을 Quill 대신 원문(HTML)으로 편집 중인지 — Quill 이 못 담는 본문은 여기로 연다. 「그래도 Quill」이 false 로 내린다. */
  useRawHtmlEditor = false;
  private descriptionModeSubscription: Subscription;

  constructor(
    private cds: CheckDesktopService,
    public fileUploadService: FileUploadService,
    private formService: FormService,
    private messageService: MessageService,
    private authService: AuthService,
    private router: Router,
    private productService: ProductService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.productId && this.isAdminPage && this.productId) this.loadDiscountCouponCandidates(this.productId);
  }

  private discountCandidatesSub?: Subscription;

  /** 지정 쿠폰이 삭제되는 등 후보가 바뀌는 경우가 있어, 저장이 성공할 때마다 다시 부를 수 있게 공개 메서드로 둔다.
   *  ngOnChanges 로 productId 가 바뀌며 재호출될 수 있어, v2 드로어와 같은 레이스 가드(구독 취소 + 콜백에서 productId
   *  재확인)를 둔다. */
  loadDiscountCouponCandidates(productId: number): void {
    this.discountCandidatesSub?.unsubscribe();
    this.discountCandidatesLoading = true;
    this.discountCandidatesFailed = false;
    this.discountCandidatesSub = this.productService
      .getDiscountCouponCandidates(productId)
      .pipe(failIfSwallowed())
      .subscribe({
        next: ({ candidates, autoPrice }) => {
          if (productId !== this.productId) return;
          this.discountCouponCandidates = candidates;
          this.discountAutoPrice = autoPrice;
          this.discountCandidatesLoading = false;
          this.refreshDiscountCouponSelectOptions();
        },
        error: () => {
          if (productId !== this.productId) return;
          this.discountCouponCandidates = [];
          this.discountAutoPrice = null;
          this.discountCandidatesLoading = false;
          this.discountCandidatesFailed = true;
          this.refreshDiscountCouponSelectOptions();
        },
      });
  }

  // 예약 마감 시간 드롭다운 라벨: 24시간 단위로 떨어질 때만 "(N일)" 병기
  private formatReservationHourLabel(hours: number): string {
    return hours > 0 && hours % 24 === 0 ? `${hours}시간 (${hours / 24}일)` : `${hours}시간`;
  }

  /**
   * 수정 화면은 폼을 빈 값으로 먼저 만들고 상품 조회가 끝난 뒤 description 을 setValue 한다 —
   * ngOnInit 한 번으로는 못 잡으니 처음 채워지는 값을 한 번 더 본다. 그 뒤로는 사용자의 선택을 건드리지 않는다.
   */
  private watchDescriptionMode(): void {
    const control = this.formGroup?.get('description');
    if (!control) return;
    this.useRawHtmlEditor = isRichHtml(control.value);
    if (this.useRawHtmlEditor) return;
    this.descriptionModeSubscription = control.valueChanges
      .pipe(
        filter((v) => !!v),
        take(1),
      )
      .subscribe((v) => {
        this.useRawHtmlEditor = isRichHtml(v);
      });
  }

  useQuillForDescription(): void {
    this.messageService.popup({
      description:
        '이 상세 설명은 HTML 로 만들어져 있습니다. Quill 편집기로 저장하면 표·박스·정렬 같은 ' +
        '구성이 사라지고 글자만 남으며, 되돌릴 수 없습니다. 그래도 여시겠습니까?',
      cancelLabel: '취소',
      confirmLabel: 'Quill 로 편집',
      confirmBtnClass: 'primary',
      confirmHandler: () => {
        this.useRawHtmlEditor = false;
      },
    });
  }

  ngOnInit() {
    // Check if we're in admin page
    this.isAdminPage = this.router.url.includes('/admin/');

    this.userSubscription = this.authService.getCurrentUserFromServer().subscribe((user) => {
      this.isAdmin = user.role === 'admin';
    });

    if (this.isAdminPage && !this.reservationHours.some((option) => option.value === 0)) {
      this.reservationHours = [{ label: this.formatReservationHourLabel(0), value: 0 }, ...this.reservationHours];
    }

    // 상대 시간 업데이트를 위한 interval 설정 (30초마다)
    this.timeUpdateInterval = setInterval(() => {
      // Angular change detection이 자동으로 발동되어 getRelativeTime()이 재실행됨
    }, 30000);

    this.watchDescriptionMode();

    this.descriptions = [
      {
        title: '상품을 잘 표현하는 직관적인 한 문장을 만들어 주세요',
        descList: [
          '제목은 노출되는 상품 목록에서 고객에게 어필할 수 있는 첫 문구입니다.',
          '상품 주제를 정확하고 간결하게 표현해 주세요.',
          '지역명, 일정 등의 정보가 포함된 제목은 전달에 더욱 효과적입니다.',
        ],
        moreDescList: [
          '어떤 제목이 좋을지 여전히 고민이신가요?',
          '<a target="_blank" href="https://nnbhost.super.site/상품-관리하기/01-상품-제목">제목 가이드</a>에서 다양한 사례와 팁을 참고해 보세요!',
        ],
      },
      {
        title: '고객의 클릭을 유도하는 대표 이미지를 등록해 주세요',
        descList: [
          '상품 목록에서 고객에게 제일 먼저 노출되는 대표 이미지입니다.',
          '상품의 특성이 잘 드러난 이미지가 좋습니다.',
          '텍스트가 포함된 이미지 사용은 되도록 지양해 주세요.',
          '이미지 사이즈는 1080 x 1080px 또는 1:1 비율로 만들어 주세요.',
        ],
        moreDescList: [
          '대표 이미지가 상품의 첫 인상을 결정해요!',
          '<a target="_blank" href="https://nnbhost.super.site/상품-관리하기/02-대표-이미지">대표 이미지 가이드</a>에서 다양한 사례와 팁을 참고해 보세요!',
        ],
      },
      {
        title: '만남을 위한 구체적인 장소와 소요시간을 알려주세요.',
        descList: [
          '주소에는 도로명 주소를, 상세 장소에는 구체적인 장소 명을 작성해주세요.',
          '이 장소에 오기 위해서 도보, 대중교통, 자차 등으로 어떻게 오면 좋은지 알려주세요.',
          '상품 진행시간은 상품을 진행하는데 걸리는 총 소요 시간을 작성해주시면 됩니다.',
          '예약 가능 시간은 최소 몇 시간 전까지 예약이 가능한지를 작성해주시면 됩니다.',
        ],
        moreDescList: [
          '<a target="_blank" href="https://nnbhost.super.site/상품-관리하기/03-장소시간">장소/시간 가이드</a>의 팁을 참고해 보세요!',
        ],
      },
      {
        title: '상품에 참여하기 위한 가격을 설정해 주세요',
        descList: [
          '메인 화면에 노출되는 가격입니다.',
          '가격의 자릿수 구분을 위해 [,]이나 [.]을 사용하지 말고 숫자만 기입해 주세요.',
          "가격에는 상품의 원래 가격을 기입해 주세요. (무료 상품인 경우 '0'을 기입해 주세요)",
          '할인은 쿠폰 지정으로 정해집니다. 지정하지 않으면 받을 수 있는 쿠폰 중 최대 할인이 자동 적용돼요.',
        ],
        moreDescList: [
          '<a target="_blank" href="https://nnbhost.super.site/상품-관리하기/04-가격">가격 가이드</a>의 팁을 참고해 보세요!',
        ],
      },
      {
        title: '상품을 소개하는 매력적인 글과 이미지를 기입해 주세요.',
        descList: [
          '맞춤법이 맞지 않거나 오타가 있을 경우 임의로 수정될 수 있으며, 검수 절차로 인해 상품 판매가 늦어질 수 있습니다. ',
          '너무 긴 글과 많은 사진 사용은 오히려 가독성을 떨어뜨려 쉽게 피로를 유발합니다.',
          '참여자들이 어떤 즐거운 경험을 할 수 있는지, 무엇을 얻어갈 수 있는지 상상해볼 수 있도록 작성해 주세요.',
        ],
        moreDescList: [
          '어떻게 써야 할지 도저히 감이 잡히지 않는다면',
          '<a target="_blank" href="https://nnbhost.super.site/상품-관리하기/05-소개">소개 가이드</a>의 팁을 참고해 보세요!',
        ],
      },
      {
        title: '참여자가 직접 준비해야 할 것을 기입해 주세요.',
        descList: [
          '원활한 진행을 위해 참여자가 꼭 준비해야 할 것을 적어주세요.',
          '깜빡 잊은 바람에 못 챙겨올 수 있는 물건을 세심하게 한 번 더 챙겨주셔도 좋아요.',
          '상품에 포함되는 사항과 불포함되는 사항을 명시해주면 참여자들이 상품을 이해하는 데 훨씬 쉬울 거예요.',
          '숙소 상품이라면 숙소 시설을 선택하여 표시할 수도 있습니다.',
        ],
        moreDescList: [
          '<a target="_blank" href="https://nnbhost.super.site/상품-관리하기/06-준비물">준비물</a>의 팁을 참고해 보세요!',
        ],
      },
      {
        title: '참여자에게 당부하거나 중요하게 안내하고 싶은 사항에 관해 기입해 주세요.',
        descList: [
          '최소 인원 모객 미달 또는 천재 지변으로 인한 취소 등 안내사항에 관해 언급해 주세요.',
          "딱히 유의사항이 생각나지 않는다면 '노는법 정책을 따르겠습니다'를 체크해 주세요.",
          '노는법의 유의사항 기본 정책에 추가 또는 삭제 하셔도 괜찮습니다.',
          '유의사항은 추후 참여자와 문제 발생시 중요하게 다뤄지는 증빙자료이기 때문에 신중히 작성해 주세요.',
        ],
        moreDescList: [
          '<a target="_blank" href="https://nnbhost.super.site/상품-관리하기/07-유의사항">유의사항</a>의 팁을 참고해 보세요!',
        ],
      },
      {
        title: '상품 시작일을 기준으로 환불정책을 기입해 주세요.',
        descList: [
          '상품 시작일 자정을 기준으로 환불 정책을 기입해주세요.',
          '예시)',
          '상품 시작일 기준 5일 이전까지(~6일 전) 100% 환불',
          '상품 시작일 기준 1일 전까지(1~5일전) 50% 환불',
          '상품 시작일 기준 당일 이내 환불 불가',
        ],
        moreDescList: [
          '<a target="_blank"  href="https://nnbhost.super.site/상품-관리하기/08-환불정책">환불정책</a>의 팁을 참고해 보세요!',
        ],
      },
      {
        title: '상품의 상세페이지 내용을 마지막으로 한 번 더 확인해 주세요',
        descList: [
          '최종 확인 페이지가 나오지 않는다면 아직 작성이 안 된 부분이 있는 것이니, 한 번 더 꼼꼼히 확인해 주세요.',
          '상품 최종 확인 페이지에 문제가 없다면 완료 버튼을 눌러주세요!',
          '상품 등록이 완료되면 승인을 위한 심사가 진행됩니다. (영업일 기준 3~5일 정도 소요되며, 승인 또는 반려 결과를 안내드립니다.)',
        ],
      },
    ];
    this.cds.isDesktop.subscribe((resp) => (this.isDesktop = resp));
    for (let i = 0; i < 31; i++) {
      this.days.push(i);
    }
    for (let i = 0; i < 24; i++) {
      this.hours.push(i);
    }
    for (let i = 0; i < 60; i += 10) {
      this.minutes.push(i);
    }

    // 미리보기 초기화
    this.initializeLivePreview();
    this.setupFormChangeTracking();
  }

  ngOnDestroy() {
    if (this.formChangeSubscription) {
      this.formChangeSubscription.unsubscribe();
    }
    if (this.descriptionModeSubscription) {
      this.descriptionModeSubscription.unsubscribe();
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
    }
  }

  /**
   * 미리보기 토글
   */
  togglePreview() {
    this.showPreview = !this.showPreview;
    if (this.showPreview) {
      this.updateLivePreview();
    }
  }

  /** 라이브 프리뷰용 해시태그 매핑 — 폼 값(id/문자/객체 혼재)을 Hashtag로 변환. 버전 1/2 경로가 공유한다. */
  private mapPreviewHashtags(hashtags?: (number | string | Hashtag)[]): (Hashtag | undefined)[] {
    return (
      hashtags?.map((h) => {
        if (typeof h === 'number') {
          return this.hashtags?.find((v) => v.id === h);
        } else if (typeof h === 'string') {
          return new Hashtag(undefined, h);
        } else {
          return h;
        }
      }) || []
    );
  }

  private makeLivePreviewProduct(form: UntypedFormGroup) {
    if (form.value.version === 1) {
      const {
        version,
        title,
        hashtags,
        photos,
        address,
        detailAddress,
        howToCome,
        runningDays,
        runningHours,
        runningMinutes,
        reservationHours,
        price,
        point,
        recommend,
        description,
        courses,
        amenities,
        notice,
        checkList,
        includeList,
        excludeList,
        refundPolicy,
      } = form.value;
      // form.value 엔 더는 discountPrice 가 없다(NNB-912) — 어드민은 지정한 쿠폰의 적용가, 호스트는
      // 저장된 실제 값을 그대로 보여준다(둘 다 편집 대상이 아니니 폼이 아니라 여기서 채운다).
      const discountPrice = this.isAdminPage
        ? this.selectedDiscountPreviewPrice
        : (this.previewProduct?.discountPrice ?? null);
      const amenityList = this.amenities?.filter((a) => amenities.find((v) => a.id == v)) || [];
      const hashtagList = this.mapPreviewHashtags(hashtags);
      return new Product(
        0,
        title,
        price,
        discountPrice,
        recommend,
        version,
        address,
        detailAddress,
        howToCome,
        runningDays,
        runningHours,
        runningMinutes,
        reservationHours,
        this.latitude,
        this.longitude,
        undefined,
        notice,
        checkList,
        includeList,
        excludeList,
        refundPolicy,
        ProductStatus.DISABLED,
        true,
        true,
        '',
        undefined,
        description,
        point,
        undefined,
        undefined,
        undefined,
        hashtags,
        amenityList,
        0,
        false,
        undefined,
        new Date(),
        new Date(),
        photos,
        courses,
        [],
      );
    } else {
      const {
        version,
        title,
        hashtags,
        photos,
        address,
        detailAddress,
        howToCome,
        runningDays,
        runningHours,
        runningMinutes,
        reservationHours,
        price,
        recommend,
        description,
        summaries,
        sections,
        courses,
        amenities,
        notice,
        checkList,
        includeList,
        excludeList,
        refundPolicy,
      } = form.value;
      // form.value 엔 더는 discountPrice 가 없다(NNB-912) — 어드민은 지정한 쿠폰의 적용가, 호스트는
      // 저장된 실제 값을 그대로 보여준다(둘 다 편집 대상이 아니니 폼이 아니라 여기서 채운다).
      const discountPrice = this.isAdminPage
        ? this.selectedDiscountPreviewPrice
        : (this.previewProduct?.discountPrice ?? null);
      const amenityList = this.amenities?.filter((a) => amenities.find((v) => a.id == v)) || [];
      const hashtagList = this.mapPreviewHashtags(hashtags);
      const previewSummaries = toPreviewSummaries(summaries);
      const previewSections = toPreviewSections(sections);
      return new Product(
        0,
        title,
        price,
        discountPrice,
        recommend,
        version,
        address,
        detailAddress,
        howToCome,
        runningDays,
        runningHours,
        runningMinutes,
        reservationHours,
        this.latitude,
        this.longitude,
        undefined,
        notice,
        checkList,
        includeList,
        excludeList,
        refundPolicy,
        ProductStatus.DISABLED,
        true,
        true,
        '',
        undefined,
        description,
        undefined,
        previewSummaries,
        previewSections,
        undefined,
        hashtagList,
        amenityList,
        0,
        false,
        undefined,
        new Date(),
        new Date(),
        photos,
        courses,
        [],
      );
    }
  }

  /**
   * 실시간 미리보기 초기화
   */
  private initializeLivePreview() {
    // previewProduct를 기반으로 초기화
    if (!this.formGroup) return;
    this.livePreviewData = this.makeLivePreviewProduct(this.formGroup);
  }

  /**
   * 폼 변경 추적 설정
   */
  private setupFormChangeTracking() {
    if (this.formGroup) {
      this.formChangeSubscription = this.formGroup.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        if (this.showPreview) {
          this.updateLivePreview();
        }
      });
    }
  }

  /**
   * 실시간 미리보기 업데이트
   */
  private updateLivePreview() {
    if (!this.formGroup) return;
    this.livePreviewData = this.makeLivePreviewProduct(this.formGroup);
  }

  /**
   * 폼 값 변경 시 미리보기 강제 업데이트
   */
  updatePreviewManually() {
    if (this.showPreview) {
      this.updateLivePreview();
    }
  }

  /**
   * 이미지 보정 완료 후 FormArray 업데이트
   * representation photos, section photos, summary photos, price image, description 모두 지원
   */
  handlePhotoUpdate(event: {
    type: 'representation' | 'section' | 'summary' | 'price' | 'description';
    index: number;
    sectionIndex?: number;
    paragraphIndex?: number;
    newUrl: string;
    oldUrl?: string;
    updatedHtml?: string;
  }) {
    if (event.type === 'description') {
      // 상세설명 HTML 내 이미지 업데이트
      if (event.updatedHtml) {
        this.formGroup.get('description')?.setValue(event.updatedHtml);
      }
    } else if (event.type === 'representation') {
      if (!this.replacePhoto(controlAt(this.formGroup.get('photos'), event.index), event)) return;
    } else if (event.type === 'section') {
      const section = controlAt(this.formGroup.get('sections'), event.sectionIndex);
      const paragraph = controlAt(section?.get('paragraphs'), event.paragraphIndex);
      if (!this.replacePhoto(controlAt(paragraph?.get('photos'), event.index), event)) return;
    } else if (event.type === 'summary') {
      const summary = controlAt(this.formGroup.get('summaries'), event.index);
      if (!this.replacePhoto(controlAt(summary?.get('photos'), 0), event)) return;
    } else if (event.type === 'price') {
      // 가격 이미지 업데이트
      const priceImage = this.formGroup.get('priceImage');
      if (!isExpectedPhotoTarget(priceImage?.value, event.oldUrl)) {
        this.messageService.toast(PHOTO_MOVED_MESSAGE);
        return;
      }
      priceImage?.setValue(event.newUrl);
    }

    // 미리보기 즉시 업데이트
    this.updateLivePreview();
  }

  /** 미리보기 갱신은 폼보다 늦으므로, 보정을 기다리는 사이 그 자리가 비었거나 다른 사진이 들어왔으면 적용하지 않고 알린다. */
  private replacePhoto(control: AbstractControl | undefined, event: { newUrl: string; oldUrl?: string }): boolean {
    if (!control || !isExpectedPhotoTarget(control.value, event.oldUrl)) {
      this.messageService.toast(PHOTO_MOVED_MESSAGE);
      return false;
    }
    control.patchValue({ photo: event.newUrl });
    return true;
  }

  /**
   * v1 상품의 소개 폼을 v2 구성으로 바꾼다. 내용은 옮기지 않고 비운다.
   * 이 메서드는 보관본을 만들거나 복원하지 않는다. 변환본은 별도 스크립트가 만든
   * '(V2 강제변환)' 보관본을 복원해야 한다.
   *
   * v2 → v1 로 되돌리는 경로는 없앴다. 신규 작성은 v2 뿐이고, v1 로 내려가면 요약·섹션이
   * 폼에서 통째로 사라지는데 그건 되돌릴 수단 없이 잃는 길이었다.
   */
  convertToV2() {
    this.messageService.popup({
      description: '기존 작성한 내용이 사라집니다. 계속하시겠습니까?',
      cancelLabel: '취소',
      confirmLabel: '확인',
      confirmBtnClass: 'primary',
      confirmHandler: () => {
        this.formGroup.controls.version.setValue(2);
        this.formGroup.setControl('summaries', new UntypedFormArray([this.newSummary()]));
        this.formGroup.setControl('sections', new UntypedFormArray([this.newSection()]));
        this.formGroup.removeControl('point');
        this.formGroup.controls.description.reset();
      },
    });
  }

  moveUp(index: number, formGroupName: string) {
    const formArray = this.formGroup.get(formGroupName) as UntypedFormArray;
    const control = formArray.at(index);
    formArray.removeAt(index);
    formArray.insert(index - 1, control);
  }

  moveDown(index: number, formGroupName: string) {
    const formArray = this.formGroup.get(formGroupName) as UntypedFormArray;
    const control = formArray.at(index + 1);
    formArray.removeAt(index + 1);
    formArray.insert(index, control);
  }

  moveUp2(sectionIndex, index: number, formGroupName: string) {
    const formArray = this.getSection().at(sectionIndex).get(formGroupName) as UntypedFormArray;
    const control = formArray.at(index);
    formArray.removeAt(index);
    formArray.insert(index - 1, control);
  }

  moveDown2(sectionIndex, index: number, formGroupName: string) {
    const formArray = this.getSection().at(sectionIndex).get(formGroupName) as UntypedFormArray;
    const control = formArray.at(index + 1);
    formArray.removeAt(index + 1);
    formArray.insert(index, control);
  }

  // summary
  getSummary(): UntypedFormArray {
    return this.formGroup.get('summaries') as UntypedFormArray;
  }

  newSummary(): UntypedFormGroup {
    return new UntypedFormGroup({
      photos: new UntypedFormArray([], Validators.required),
      title: new UntypedFormControl('', this.formService.getValidators(50)),
      description: new UntypedFormControl('', this.formService.getValidators(500)),
    });
  }

  addSummary() {
    this.getSummary().push(this.newSummary());
  }

  removeSummary(index: number) {
    this.messageService.popup({
      description: '정말로 삭제하시겠습니까?',
      confirmBtnClass: 'primary',
      confirmLabel: '확인',
      confirmHandler: () => {
        this.getSummary().removeAt(index);
      },
      cancelLabel: '취소',
    });
  }

  // sections
  getSection(): UntypedFormArray {
    return this.formGroup.get('sections') as UntypedFormArray;
  }

  newSection(): UntypedFormGroup {
    return new UntypedFormGroup({
      title: new UntypedFormControl('', this.formService.getValidators(50)),
      paragraphs: new UntypedFormArray([this.newParagraph()]),
    });
  }

  addSection() {
    this.getSection().push(this.newSection());
  }

  removeSection(sectionIndex: number) {
    this.messageService.popup({
      description: '정말로 삭제하시겠습니까?',
      confirmBtnClass: 'primary',
      confirmLabel: '확인',
      confirmHandler: () => {
        this.getSection().removeAt(sectionIndex);
      },
      cancelLabel: '취소',
    });
  }

  // paragraphs
  getParagraph(sectionIndex: number): UntypedFormArray {
    return this.getSection().at(sectionIndex).get('paragraphs') as UntypedFormArray;
  }

  newParagraph(): UntypedFormGroup {
    return new UntypedFormGroup({
      title: new UntypedFormControl('', this.formService.getValidators(50)),
      time: new UntypedFormControl('', Validators.maxLength(50)),
      photos: new UntypedFormArray([]),
      description: new UntypedFormControl('', [Validators.required, Validators.maxLength(500)]),
      note: new UntypedFormControl(''),
    });
  }

  addParagraph(sectionIndex: number) {
    this.getParagraph(sectionIndex).push(this.newParagraph());
  }

  removeParagraph(sectionIndex: number, paragraphIndex: number) {
    this.messageService.popup({
      description: '정말로 삭제하시겠습니까?',
      confirmBtnClass: 'primary',
      confirmLabel: '확인',
      confirmHandler: () => {
        this.getParagraph(sectionIndex).removeAt(paragraphIndex);
      },
      cancelLabel: '취소',
    });
  }

  // courses
  getCourse(): UntypedFormArray {
    return this.formGroup.get('courses') as UntypedFormArray;
  }

  newCourse(): UntypedFormGroup {
    return new UntypedFormGroup({
      title: new UntypedFormControl('', this.formService.getValidators(20)),
      schedules: new UntypedFormArray([this.newSchedule()]),
    });
  }

  addCourse() {
    this.getCourse().push(this.newCourse());
  }

  removeCourse(courseIndex: number) {
    this.messageService.popup({
      description: '정말로 삭제하시겠습니까?',
      confirmBtnClass: 'primary',
      confirmLabel: '확인',
      confirmHandler: () => {
        this.getCourse().removeAt(courseIndex);
      },
      cancelLabel: '취소',
    });
  }

  // schedules
  getSchedule(courseIndex: number): UntypedFormArray {
    return this.getCourse().at(courseIndex).get('schedules') as UntypedFormArray;
  }

  newSchedule(): UntypedFormGroup {
    return new UntypedFormGroup({
      subTitle: new UntypedFormControl('', this.formService.getValidators(20)),
      content: new UntypedFormControl('', Validators.required),
      note: new UntypedFormControl(''),
    });
  }

  addSchedule(courseIndex: number) {
    this.getSchedule(courseIndex).push(this.newSchedule());
  }

  removeSchedule(courseIndex: number, scheduleIndex: number) {
    this.messageService.popup({
      description: '정말로 삭제하시겠습니까?',
      confirmBtnClass: 'primary',
      confirmLabel: '확인',
      confirmHandler: () => {
        this.getSchedule(courseIndex).removeAt(scheduleIndex);
      },
      cancelLabel: '취소',
    });
  }

  searchMap(event) {
    this.onChangeAddressEvent.emit(event);
  }

  checkAllowPolicy(event) {
    const isChecked = event.target.checked;
    this.noticeChecked = isChecked;
    const value = `✔ 예약 완료 시 카카오톡 안내 메시지가 발송되니 꼭 확인해 주세요.
✔ 여성 없이 남성 만으로 이루어진 팀은 방문하실 수 없습니다.
✔ 일정 중 발생할 수 있는 모든 안전사고에 유의 하시기 바라며, 이용자 본인의 과실로 인한 안전사고의 경우는 이용자 본인이 책임을 지게 됩니다.
✔ 예약한 모임의 일정 변경이나 취소를 원할 시 상세페이지 하단의 ‘환불 규정’을 따르며, 원칙상 당일취소는 불가합니다. 환불 규정을 꼭 숙지해 주세요.
✔ 최소 인원 미달로 인한 상품 진행 불가 취소 시 참가비는 전액 환불됩니다.
✔ 기상 및 천재지변, 호스트님의 상황으로 인한 취소 시 전액 환불됩니다.`;
    if (isChecked) {
      if (this.formGroup.controls.notice.value !== '') {
        this.messageService.popup({
          title: '유의사항',
          description: '기존 작성한 유의사항 내용이 사라집니다.',
          confirmLabel: '확인',
          confirmBtnClass: 'primary',
          confirmHandler: () => {
            this.formGroup.controls.notice.setValue(value);
          },
          cancelLabel: '취소',
          cancelHandler: () => {
            setTimeout(() => (this.noticeChecked = false));
          },
        });
      } else {
        this.formGroup.controls.notice.setValue(value);
      }
    }
  }

  ngAfterViewInit(): void {
    this.onQuillLoadEvent.emit(this.quill);
    if (this.showPreview && this.isDesktop) {
      this.updateLivePreview();
    }
  }

  showMenu() {
    this.isShowMenu = !this.isShowMenu;
  }

  getRelativeTime(date: Date): string {
    if (!date) return '';

    const timeAgo = dayjs(date).fromNow();
    return `${timeAgo} 저장됨`;
  }

  next() {
    if (this.currentStep < 8) {
      this.currentStep++;
      this.onNextEvent.emit();
    }
  }

  prev() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.onPrevEvent.emit();
    }
  }

  lastCheck() {
    this.currentStep = 8; // Set to final step
    this.onLastCheckEvent.emit();
  }

  checkHashtag(event) {
    this.addTag = event.term.length <= this.MAX_HASHTAG_LENGTH;
  }

  imageCropped(event: { url: string; file: File }) {
    this.croppedImage = new ProductRepresentationPhotoCreateDto(event.url, 0, event.file);
  }

  /**
   * "지정 안 함(자동)" + 연결된 쿠폰들. 적격이 아닌 쿠폰도 보여준다 — 지정해도 서버가 자동선택으로 폴백한다.
   *
   * getter 가 아니라 필드다 — ng-select 의 `[selectList]` 는 매 변경감지마다 새 배열을 받으면
   * dev 모드에서 `ExpressionChangedAfterItHasBeenCheckedError` 를 띄운다. 후보가 실제로 바뀌는
   * 시점(`loadDiscountCouponCandidates`)에만 다시 계산해 참조를 고정한다.
   */
  discountCouponSelectOptions: { id: number | null; label: string }[] = [
    { id: null, label: '지정 안 함(자동) — 할인 없음' },
  ];
  private refreshDiscountCouponSelectOptions(): void {
    const autoLabel = this.discountAutoPrice != null ? `${this.discountAutoPrice.toLocaleString()}원` : '할인 없음';
    this.discountCouponSelectOptions = [
      { id: null, label: `지정 안 함(자동) — ${autoLabel}` },
      ...this.discountCouponCandidates.map((candidate) => ({
        id: candidate.id,
        label: candidate.eligible
          ? `${candidate.name} — ${(candidate.estimatedPrice ?? 0).toLocaleString()}원`
          : `${candidate.name} (지금 받을 수 없음)`,
      })),
    ];
  }

  /** 지정한 쿠폰의 적용가. 적격이 아니면(또는 지정 안 함이면) 자동선택가로 보여준다. */
  get selectedDiscountPreviewPrice(): number | null {
    const discountCouponId = this.formGroup?.controls?.discountCouponId?.value ?? null;
    if (discountCouponId == null) return this.discountAutoPrice;
    const selected = this.discountCouponCandidates.find((candidate) => candidate.id === discountCouponId);
    return selected?.estimatedPrice ?? this.discountAutoPrice;
  }

  dispatch() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    });
    // 미리보기 업데이트
    this.updatePreviewManually();
  }

  add() {
    this.onAddEvent.emit();
  }

  onCheckAmenity(event, amenity: Amenity) {
    const formArray: UntypedFormArray = this.formGroup.controls.amenities as UntypedFormArray;
    if (event.target.checked) {
      formArray.push(new UntypedFormControl(+event.target.value));
    } else {
      const index = formArray.controls.findIndex((control) => control.value === amenity.id);
      formArray.removeAt(index);
    }
  }

  onIsAmenity(event) {
    const control = this.formGroup.controls.isAmenity;
    if (event.target.checked) {
      control.setValue(true);
    } else {
      this.messageService.popup({
        title: '숙소 시설 정보',
        description: '숙소 시설 정보가 초기화됩니다.',
        confirmLabel: '확인',
        confirmBtnClass: 'primary',
        confirmHandler: () => {
          control.setValue(false);
          (this.formGroup.controls.amenities as UntypedFormArray).clear();
        },
        cancelLabel: '취소',
        cancelHandler: () => {
          control.setValue(true);
        },
      });
    }
  }
}
